import React, {
  ReactNode, useEffect, useRef, useState,
} from 'react';
import './index.scss';

export type ToastPlacement = 'bottom-center' | 'bottom-right';

interface ToastProps {
  /** Texto do aviso. Vazio esconde o toast. */
  message: string;
  /**
   * Chamado uma vez quando o aviso expira. Deve limpar `message` (ex.:
   * `() => setMessage('')`), senão o próximo aviso não aparece se tiver o mesmo texto.
   */
  onClose: () => void;
  /** Tempo, em ms, até o aviso sumir. */
  duration?: number;
  placement?: ToastPlacement;
  /** Elemento exibido antes do texto (ex.: ícone de check). */
  icon?: ReactNode;
  /** Classe extra no elemento visual do aviso (o mesmo de `.ds-toast`). */
  className?: string;
}

/**
 * Aviso flutuante (fixed) com auto-dismiss.
 *
 * Controlado pelo pai via `message`: cada mensagem nova reinicia a contagem de
 * `duration`; ao expirar, o toast some e chama `onClose` uma vez. Trocar só a
 * identidade de `onClose` (ex.: arrow function inline) não reinicia a contagem.
 *
 * O `onClose` do pai deve limpar `message`: o toast só reaparece quando
 * `message` muda, e um `setMessage` com o mesmo texto que ficou na prop depois
 * de expirar não muda nada, então esse aviso não volta.
 *
 * A região `role="status"` (live region polite) fica sempre montada, vazia e sem
 * visual enquanto não há aviso; o elemento visual entra dentro dela. Assim o
 * leitor de tela já conhece a região quando o texto chega e anuncia o aviso.
 * Por isso o pai renderiza o Toast sempre, com `message=''` quando não há aviso:
 * montá-lo só junto com a mensagem (`{msg && <Toast .../>}`) faz a região nascer
 * com o texto, e o anúncio fica instável.
 */
export default function Toast({
  message,
  onClose,
  duration = 3000,
  placement = 'bottom-center',
  icon = null,
  className = '',
}: ToastProps) {
  const onCloseRef = useRef(onClose);
  const [expired, setExpired] = useState(false);
  const [shownMessage, setShownMessage] = useState(message);

  // Mensagem nova (inclusive a mesma depois de limpa) volta a ficar visível.
  if (message !== shownMessage) {
    setShownMessage(message);
    setExpired(false);
  }

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!message || expired) return undefined;
    const timer = setTimeout(() => {
      setExpired(true);
      onCloseRef.current();
    }, duration);
    return () => clearTimeout(timer);
  }, [message, duration, expired]);

  const visible = Boolean(message) && !expired;

  return (
    <div className="ds-toast-region" role="status">
      {visible && (
        <div className={`ds-toast ds-toast--${placement} ${className}`.trim()}>
          {icon}
          <span className="ds-toast-message">{message}</span>
        </div>
      )}
    </div>
  );
}
